import { useId, type ReactNode } from "react";
import { cx } from "../../cx";

export interface RadioCardOption<V extends string = string> {
  readonly value: V;
  readonly title: string;
  readonly description?: string;
  /** Extra content under the description (badges, notes). */
  readonly extra?: ReactNode;
  readonly icon?: ReactNode;
  readonly disabled?: boolean;
}

export interface RadioCardGroupProps<V extends string = string> {
  /** Rendered as the `<legend>` of the fieldset (required for accessibility). */
  readonly legend: string;
  readonly hideLegend?: boolean;
  readonly name?: string;
  readonly options: readonly RadioCardOption<V>[];
  /** `null` = nothing chosen yet (no option is pre-selected). */
  readonly value: V | null;
  readonly onChange: (value: V) => void;
  readonly disabled?: boolean;
  /** Error message; linked to the group with `aria-describedby`. */
  readonly error?: string;
  readonly className?: string;
}

/**
 * A group of native radio buttons rendered as selectable cards (keyboard: arrows move and
 * select, like any radio group). The cards flow with the writing direction via CSS grid.
 */
export function RadioCardGroup<V extends string = string>({
  legend,
  hideLegend = false,
  name,
  options,
  value,
  onChange,
  disabled = false,
  error,
  className,
}: RadioCardGroupProps<V>) {
  const autoId = useId();
  const groupName = name ?? `radio-${autoId}`;
  const errorId = error ? `${groupName}-error` : undefined;
  return (
    <fieldset
      className={cx("flex flex-col gap-3", className)}
      aria-describedby={errorId}
      disabled={disabled}
    >
      <legend className={cx("mb-3 text-sm font-medium text-fg", hideLegend && "sr-only")}>
        {legend}
      </legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const id = `${groupName}-${option.value}`;
          const checked = value === option.value;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cx(
                "flex cursor-pointer items-start gap-3 rounded-lg border bg-surface p-4 transition-colors",
                "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus",
                checked
                  ? "border-action-primary bg-action-ghost-hover"
                  : "border-line hover:border-line-strong",
                (disabled || option.disabled) && "cursor-not-allowed opacity-70",
              )}
            >
              <input
                id={id}
                type="radio"
                name={groupName}
                value={option.value}
                checked={checked}
                disabled={disabled || option.disabled === true}
                onChange={() => onChange(option.value)}
                className="mt-1 size-4 shrink-0 accent-action-primary focus-visible:outline-none"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-fg">
                  {option.icon}
                  {option.title}
                </span>
                {option.description && (
                  <span className="text-sm text-fg-muted">{option.description}</span>
                )}
                {option.extra}
              </span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="text-xs text-danger-fg">
          {error}
        </p>
      )}
    </fieldset>
  );
}
